/*
 * Cassaforte — importar desde otro gestor o navegador.
 * Usa el servicio de importación de Psono: el archivo se lee y se cifra en el navegador.
 */
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import importService from "../../services/import";

interface Source { id: string; name: string; format: string; accept: string; help: string }

// Fuentes más comunes primero; el resto queda en "Otro formato"
const SOURCES: Source[] = [
	{ id: "chrome_csv", name: "Google Chrome", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_CHROME" },
	{ id: "safari_csv", name: "Safari / Apple", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_SAFARI" },
	{ id: "firefox_csv", name: "Firefox", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_FIREFOX" },
	{ id: "bitwarden_json", name: "Bitwarden", format: "JSON", accept: ".json", help: "CF_IMP_HELP_BITWARDEN" },
	{ id: "one_password_v8", name: "1Password", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_1PASSWORD" },
	{ id: "lastpass_com_csv", name: "LastPass", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_LASTPASS" },
	{ id: "protonpass_csv", name: "Proton Pass", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_PROTON" },
	{ id: "dashlane_csv", name: "Dashlane", format: "CSV", accept: ".csv", help: "CF_IMP_HELP_DASHLANE" },
	{ id: "keepass_info_xml", name: "KeePass", format: "XML", accept: ".xml", help: "CF_IMP_HELP_KEEPASS" },
];
const MAIN_IDS = SOURCES.map((s) => s.id);

/* El servicio emite eventos globales: los conectamos una sola vez y reenviamos al componente activo */
const listeners: { progress?: (done: number, total: number) => void } = {};
let wired = false;
let opened = 0;
let closed = 0;
function wire() {
	if (wired) return;
	wired = true;
	importService.on("import-started", () => { opened = 0; closed = 0; });
	importService.on("create-secret-started", () => { opened++; listeners.progress?.(closed, opened); });
	importService.on("create-secret-complete", () => { closed++; listeners.progress?.(closed, opened); });
}

const XIcon = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M6 6l12 12M18 6 6 18"/></svg>;

export const ImportSheet = ({ onClose, onDone }: { onClose: () => void; onDone: () => void }) => {
	const { t } = useTranslation();
	const [step, setStep] = useState<"pick" | "file" | "working" | "done">("pick");
	const [source, setSource] = useState<Source | null>(null);
	const [fileName, setFileName] = useState("");
	const [text, setText] = useState("");
	const [binary, setBinary] = useState<ArrayBuffer | undefined>(undefined);
	const [progress, setProgress] = useState({ done: 0, total: 0 });
	const [error, setError] = useState("");
	const [drag, setDrag] = useState(false);
	const fileRef = useRef<HTMLInputElement>(null);

	const others = importService.getImporter().filter((imp) => !MAIN_IDS.includes(imp.value) && imp.value !== "psono_pw_json");

	const choose = (s: Source) => { setSource(s); setStep("file"); setError(""); setFileName(""); setText(""); };
	const chooseOther = (value: string) => {
		const imp = importService.getImporter().find((i) => i.value === value);
		if (imp) choose({ id: imp.value, name: imp.name, format: "", accept: "", help: "CF_IMP_HELP_OTHER" });
	};

	const readFile = (file: File) => {
		setFileName(file.name); setError("");
		const reader = new FileReader();
		reader.onload = (e) => {
			const buf = e.target?.result as ArrayBuffer;
			setBinary(buf);
			setText(new TextDecoder("utf-8").decode(new Uint8Array(buf)));
		};
		reader.onerror = () => setError(t("CF_IMP_READ_ERROR"));
		reader.readAsArrayBuffer(file);
	};

	const start = () => {
		if (!source || !text) return;
		wire();
		listeners.progress = (done, total) => setProgress({ done, total });
		setStep("working"); setError("");
		importService.importDatastore(source.id, text, binary).then(
			() => { listeners.progress = undefined; setStep("done"); setText(""); setBinary(undefined); onDone(); },
			(err: { errors?: string[] }) => {
				listeners.progress = undefined;
				const key = err && err.errors && err.errors[0];
				setError(key === "FILE_FORMAT_WRONG" ? t("CF_IMP_WRONG_FORMAT", { name: source.name }) : key ? t(key) : t("CF_ERROR_SAVE"));
				setStep("file");
			},
		);
	};

	const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

	return (
		<div className="cf-scrim">
			<div className="cf-sheet" role="dialog" aria-modal="true" aria-labelledby="cfImpTitle">
				<div className="cf-shead">
					{step !== "working" && <button type="button" className="cf-sx" aria-label={t("CLOSE")} onClick={onClose}>{XIcon}</button>}
					<h3 id="cfImpTitle">{step === "done" ? t("CF_IMP_DONE_TITLE") : source && step !== "pick" ? t("CF_IMP_FROM", { name: source.name }) : t("CF_IMP_TITLE")}</h3>
					<p className="cf-lead">{step === "pick" ? t("CF_IMP_LEAD") : step === "done" ? t("CF_IMP_DONE_LEAD") : t("CF_ENCRYPTED_HERE")}</p>
				</div>
				<div className="cf-sbody">
					{step === "pick" && <>
						<div className="cf-sources">
							{SOURCES.map((s) => (
								<button key={s.id} type="button" className="cf-source" onClick={() => choose(s)}>
									<b>{s.name}</b><span>{s.format}</span>
								</button>))}
						</div>
						<label className="cf-in" style={{ marginTop: 14 }}><span>{t("CF_IMP_OTHER")}</span>
							<select className="cf-select" defaultValue="" onChange={(e) => e.target.value && chooseOther(e.target.value)}>
								<option value="" disabled>{t("CF_IMP_OTHER_PH")}</option>
								{others.map((o) => <option key={o.value} value={o.value}>{o.name}</option>)}
							</select></label>
					</>}
					{step === "file" && source && <>
						<div className="cf-howto"><h4>{t("CF_IMP_HOWTO")}</h4><p>{t(source.help)}</p><p className="cf-muted" style={{ fontSize: 13 }}>{t("CF_IMP_MENUS_VARY")}</p></div>
						<div className={"cf-drop" + (drag ? " cf-drag" : "") + (fileName ? " cf-has" : "")}
							onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
							onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) readFile(f); }}
							onClick={() => fileRef.current?.click()} role="button" tabIndex={0}
							onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileRef.current?.click(); }}>
							<input ref={fileRef} type="file" accept={source.accept || undefined} hidden onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) readFile(f); }} />
							{fileName ? <><b>{fileName}</b><span>{t("CF_IMP_CHANGE_FILE")}</span></> : <><b>{t("CF_IMP_DROP")}</b><span>{t("CF_IMP_DROP_HINT", { format: source.format || "" })}</span></>}
						</div>
						{error && <p className="cf-err">{error}</p>}
						<p className="cf-warn">{t("CF_IMP_WARN")}</p>
					</>}
					{step === "working" && <div className="cf-progress">
						<div className="cf-bar-track"><i style={{ width: pct + "%" }} /></div>
						<p>{progress.total ? t("CF_IMP_PROGRESS", { done: progress.done, total: progress.total }) : t("CF_IMP_READING")}</p>
					</div>}
					{step === "done" && <>
						<p className="cf-muted">{t("CF_IMP_DONE_TEXT", { count: progress.done })}</p>
						<p className="cf-warn">{t("CF_IMP_DELETE_FILE", { file: fileName })}</p>
					</>}
				</div>
				<div className="cf-bar">
					{step === "file" && <button type="button" className="cf-ghost" onClick={() => setStep("pick")}>{t("CF_BACK")}</button>}
					{step === "file" && <button type="button" className="cf-save" disabled={!text} onClick={start}>{t("CF_IMP_START")}</button>}
					{step === "done" && <button type="button" className="cf-save" onClick={onClose}>{t("CF_IMP_SEE_VAULT")}</button>}
					{step === "pick" && <button type="button" className="cf-ghost" onClick={onClose}>{t("CANCEL")}</button>}
				</div>
			</div>
		</div>
	);
};
